var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { motion, AnimatePresence } from 'framer-motion';
import LinkIcon from '@mui/icons-material/Link';
import { Provider } from 'root/src/shared/@types/providers';
import { useSignInWithProvider } from 'root/src/client/web/auth/hooks/useSignInWithProvider';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Badge } from 'root/src/client/v2/common/components/ui/badge';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { DiscordIcon } from 'root/src/client/v2/common/components/icons/DiscordIcon';
const DISCORD_COLOR = '#5865F2';
const itemMotion = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.18 },
};
/**
 * Discord is a `Provider` in the auth API but it is not a creator channel, so it is
 * not part of `userData.tokens` like Twitch, YouTube and TikTok are. Its linked state
 * comes from the auth API through `account.getDiscordConnection`.
 *
 * Linking is always `isLinking: true`: the auth API refuses a Discord callback that
 * is not linking, so this row requires an authenticated user by construction.
 */
export const DiscordConnectionRow = () => {
    var _a;
    const utils = trpc.useUtils();
    const connectionQuery = trpc.account.getDiscordConnection.useQuery();
    const { onSignIn: connect, isLoading: connecting } = useSignInWithProvider({
        provider: Provider.Discord,
        isLinking: true,
        popup: true,
        popupOptions: {
            onSuccess: () => __awaiter(void 0, void 0, void 0, function* () {
                yield utils.account.getDiscordConnection.invalidate();
                toast.success('Discord connected');
            }),
            onError: (error) => {
                toast.error('Could not connect Discord', { description: error.message });
            },
        },
    });
    const { isLoading } = connectionQuery;
    const isConnected = Boolean((_a = connectionQuery.data) === null || _a === void 0 ? void 0 : _a.isConnected);
    return (<motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: 'easeOut' }} className={cn('rounded-xl border border-solid p-4 transition-colors', isConnected ? 'border-white/[0.08] bg-secondary/30' : 'border-dashed border-border/50 bg-secondary/10')}>
			<div className="flex flex-col gap-3 md:flex-row md:items-center">
				<div className="flex min-w-0 items-center gap-3 md:flex-1">
					<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-solid transition-colors" style={{
            borderColor: isConnected ? `${DISCORD_COLOR}30` : 'hsl(var(--border))',
            backgroundColor: isConnected ? `${DISCORD_COLOR}10` : undefined,
            color: isConnected ? DISCORD_COLOR : 'hsl(var(--muted-foreground))',
        }}>
						<DiscordIcon className="h-5 w-5"/>
					</div>
					<div className="min-w-0 flex-1">
						<p className="text-xs font-semibold">Discord</p>
						<p className="truncate text-[11px] text-muted-foreground">
							{isConnected ? 'Linked to your DareDrop account' : 'Community chat and direct messages'}
						</p>
					</div>
				</div>
				<div className="flex flex-shrink-0 items-center justify-end gap-1.5">
					{isLoading && <Skeleton className="h-7 w-20 rounded-lg"/>}
					{!isLoading && (<AnimatePresence mode="popLayout" initial={false}>
							{isConnected ? (<motion.div layout key="connected" {...itemMotion}>
									<Badge className="border-neon-green/20 bg-neon-green/10 px-1.5 py-0 text-[9px] text-neon-green">Connected</Badge>
								</motion.div>) : (<motion.div layout key="connect" {...itemMotion}>
									<Button size="sm" variant="outline" loading={connecting} onClick={connect} className="h-7 gap-1.5 rounded-lg px-2.5 text-[11px] font-medium">
										<LinkIcon sx={{ fontSize: 14 }}/>
										Connect
									</Button>
								</motion.div>)}
						</AnimatePresence>)}
				</div>
			</div>
		</motion.div>);
};
